import angular from 'angular';
import { filter, forEach, get, includes, isEmpty, pick, set, toUpper } from 'lodash';
import moment from 'moment-timezone';

import '../../services/appointment';
import '../../services/appointment-reservation';
import '../../services/area';
import '../../services/date-handler';
import '../../services/errors';
import '../../services/holiday';
import '../../services/notifications';
import '../../services/organization';
import '../../services/validation';
import '../../services/user';
import { AreaCalendarType } from '../../utils/areaCalendarType';
import { countriesWithCodes } from '../../utils/countries-and-codes';
import { CreatedFromEnum } from '../../utils/createdFromEnum';

const module = angular.module('whyline.modals.edit-appointment', [
  'whyline.services.appointment',
  'whyline.services.appointment-reservation',
  'whyline.services.area',
  'whyline.services.errors',
  'whyline.services.validation',
  'whyline.services.notifications',
  'whyline.services.date-handler',
  'whyline.services.holiday',
  'whyline.services.organization',
  'whyline.services.user',
  'whyline.directives.overbooking',
]);

const EditAppointmentController = (
  $scope,
  $uibModalInstance,
  $timeout,
  appointment,
  area,
  $translate,
  AppointmentService,
  AppointmentReservationService,
  AreaService,
  DateHandlerService,
  ErrorService,
  HolidayService,
  NotificationService,
  PlaceService,
  ValidationService,
  OrganizationService,
  UserService,
  dialog,
) => {
  'ngInject';

  const appointmentId = appointment._id ? appointment._id : false;
  UserService.GetCurrentAsPromise().then((user) => {
    $scope.user = user;
  });
  $scope.idTypesList = PlaceService.getIdentificationsForCurrentPlace() || [];
  $scope.appointment = AppointmentService.Copy(appointment);
  const customFields = null;
  if (customFields) {
    $scope.appointment.person.customFields = customFields;
  }
  $scope.modalTitle = appointmentId ? 'edit_appointment' : 'new_appointment';
  $scope.isEditMode = !!appointmentId;
  $scope.area = area ? AreaService.Copy(area) : false;
  $scope.place = PlaceService.GetCurrent();
  $scope.format = $translate.use() === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
  $scope.countries = countriesWithCodes;
  $scope.fetchSlotsExecuted = false;
  $scope.hoursOutOfRange = false;
  $scope.customFields = [];
  $scope.changePeople = (event) => {
    $scope.people = event;
  };

  $scope.people = 1;
  $scope.overbooking = false;

  $scope.endUserPersonalData = get($scope.place, 'configuration.endUserPersonalData', null);

  const defaultPlaceId = $scope.idTypesList.find((id) => id.default);
  const isAgendaEnabled = $scope.place.configuration.canGiveAppointments;

  const validate = () => {
    if (isAgendaEnabled) {
      // if (!$scope.appointment.person.email) {
      //   NotificationService.Warning($translate.instant('mandatory_email'));
      //   return false;
      // }
      if ($scope.appointmentDate && !$scope.appointmentDate.selected) {
        NotificationService.Warning($translate.instant('no_appointment_available'));
        return false;
      }
      if ($scope.area.appointmentsConfiguration.notes.required && !$scope.appointment.notes.text) {
        NotificationService.Warning($translate.instant('mandatory_notes'));
        return false;
      }
    }

    return true;
  };

  const setMaxDate = () => {
    const todayPlace = moment().tz($scope.place.timezone).startOf('day');
    return moment(todayPlace).add(1, 'year').toDate();
  };

  const setMinDate = () => {
    const todayPlace = moment().tz($scope.place.timezone);
    return todayPlace.startOf('day').toDate();
  };

  const outOfRangeWarning = (schedule) => {
    $scope.hoursOutOfRange = false;
    const dayToCheck = $scope.appointmentDate.calendar;
    const { timezone } = $scope.area;
    const currentTime = moment.tz(timezone);
    const isToday = moment(dayToCheck).tz(timezone).isSame(currentTime, 'day');
    const selectedSchedule = moment(schedule).tz(timezone);
    const enableMinutesToCreateAnAppointment = get(
      $scope.area,
      'appointmentsConfiguration.dynamicPeriod.enableMinutesToCreateAnAppointment.enabled',
      false,
    );
    const since = get($scope.area, 'appointmentsConfiguration.dynamicPeriod.since', false);
    const until = get($scope.area, 'appointmentsConfiguration.dynamicPeriod.until', false);
    const onlyToday = !since && !until;

    if (enableMinutesToCreateAnAppointment && isToday) {
      const addMinutes = get(
        $scope.area,
        'appointmentsConfiguration.dynamicPeriod.enableMinutesToCreateAnAppointment.minutes',
        0,
      );
      const newTime = moment(currentTime).add(addMinutes, 'minutes');
      $scope.hoursOutOfRange = !moment(selectedSchedule).isSameOrAfter(newTime);
    } else if (onlyToday && !isToday) {
      $scope.hoursOutOfRange = onlyToday;
    }
  };

  const observingSchedule = () => {
    $scope.$watch('appointmentDate.selected', (newValue, oldValue) => {
      if (newValue === oldValue) {
        return;
      }
      outOfRangeWarning(newValue);
      if ($scope.slots) {
        const slot = $scope.slots.find((s) => s.hour === newValue);
        if (slot) {
          $timeout(() => ($scope.overbooking = !!slot.overbooking));
        }
      }
    });
  };

  const validateProcessConfig = () => {
    const serviceCalendarType = $scope.area.appointmentsConfiguration.areaCalendarType;
    const dayToCheck = $scope.appointmentDate.calendar;

    if (serviceCalendarType === AreaCalendarType.ForADynamicPeriod) {
      $scope.appointmentDate.showProcessMessage = !DateHandlerService.dynamicPeriod(
        $scope.area.appointmentsConfiguration.dynamicPeriod,
        $scope.daysClosed,
        dayToCheck,
        $scope.place.timezone,
      );
    } else if (serviceCalendarType === AreaCalendarType.ForAFixedPeriod) {
      $scope.appointmentDate.showProcessMessage = !DateHandlerService.fixedPeriod(
        $scope.area.appointmentsConfiguration.fixedPeriod,
        $scope.daysClosed,
        dayToCheck,
        $scope.place.timezone,
      );
    }
  };

  const buildIdentifications = (id, idType) =>
    Object.assign({}, $scope.appointment.person.identifications, {
      [`${idType}`]: { type: idType, value: id },
  });

  // Date picker
  const setCalendarOptions = () => {
    $scope.dateOptions = {
      formatYear: 'yy',
      minDate: setMinDate(),
      maxDate: setMaxDate(),
      startingDay: 1,
      dateDisabled: (date) =>
        includes($scope.daysClosed, date.date.getDay()) ||
        includes($scope.datesClosed, moment(date.date).format('DD/MM/YYYY')),
    };
  };

  const closedDays = () => {
    forEach($scope.area.schedule, (day) => {
      if (!day.open) {
        $scope.daysClosed.push(day.number);
      }
    });
    HolidayService.GetAllForArea($scope.area._id)
      .then((response) => {
        $scope.datesClosed = response.data;
      })
      .catch(ErrorService.handler);
  };

  const setDefaultValue = (type) => {
    switch (type) {
      case 'number':
        return 0;

      case 'boolean':
        return false;

      default:
        return '';
    }
  };

  const getCustomFields = () =>
    OrganizationService.GetCustomFields($scope.place.organizationId)
      .then((customFields) => {
        $scope.customFields = customFields;
        if (!$scope.isEditMode) {
          customFields.forEach((customField) => {
            set(
              $scope,
              `appointment.person.customFields[${customField.name}]`,
              setDefaultValue(customField.type),
            );
          });
        }
      })
      .catch(() => NotificationService.Error($translate.instant('default_case')));

  const validateCustomFields = () => {
    for (const field of $scope.customFields) {
      const value = $scope.appointment.person.customFields[field.name];
      const result = ValidationService.validateCustomField(field, value);
      if (!result) {
        return false;
      }
    }
    return true;
  };

  const createAppointment = () => {
    AppointmentReservationService.createAppointmentReservation(
      $scope.place._id,
      $scope.area._id,
      $scope.appointment.person,
      $scope.appointment.appointment.date,
      $scope.appointment.appointment.date,
      $scope.appointment.notes,
      CreatedFromEnum.virtualineAgenda,
      $scope.people,
      $scope.user._id,
    )
      .then((res) => {
        $scope.blockSaveButton = false;
        const overbooking = get(res, 'data.overbooking', false);
        const successMessage = overbooking ? 'overbooking_successful' : 'appointment_create_succ';
        NotificationService.Success($translate.instant(successMessage));
        $uibModalInstance.close('saved');
      })
      .catch((error) => {
        $scope.blockSaveButton = false;
        ErrorService.handler(error);
      });
  };

  const overlapAppointmentWarning = () => {
    const personData = pick($scope.appointment.person, ['email', 'id', 'idType', 'phone']);
    AppointmentService.OverlapAppointments(
      $scope.place._id,
      $scope.area._id,
      $scope.appointment.appointment.date,
      personData,
    )
      .then((res) => {
        if (res.data.length) {
          const overlapData = res.data;
          const confirmText =
            res.data.length > 1
              ? $translate.instant('more_than_one_overlap_appointment_sure_warning_message')
              : $translate
                  .instant('one_overlap_appointment_sure_warning_message')
                  .replace(
                    '{day}',
                    moment(overlapData[0].appointment.ISODate).locale($translate.use()).format('l'),
                  )
                  .replace(
                    '{time}',
                    moment(overlapData[0].appointment.ISODate)
                      .tz($scope.place.timezone)
                      .locale($translate.use())
                      .format('LT'),
                  )
                  .replace('{serviceName}', overlapData[0].serviceName.toUpperCase());
          dialog
            .confirm(confirmText)
            .then(() => createAppointment())
            .catch(() => {
              $scope.blockSaveButton = false;
            });
        } else {
          createAppointment();
        }
      })
      .catch((error) => {
        ErrorService.handler(error);
      });
  };

  $scope.init = () => {
    let selectedId = null;
    observingSchedule();

    // Set appointment editing configuration
    if (appointmentId) {
      selectedId = $scope.idTypesList.find((id) => id.key === $scope.appointment.person.idType);
      $scope.appointment.person.phone.countryCode = $scope.appointment.person.phone.countryCode
        ? $scope.appointment.person.phone.countryCode
        : toUpper($scope.place.location.countryCode);
      $scope.appointmentDate = {
        calendar: DateHandlerService.getNeutralDate($scope.appointment.appointment.date),
        selected: $scope.appointment.appointment.date,
        showProcessMessage: false,
      };
      $scope.people = $scope.appointment.people;
    } else {
      set(
        $scope.appointment,
        'person.phone.countryCode',
        toUpper($scope.place.location.countryCode),
      );
      set($scope.appointment, 'person.idType', defaultPlaceId.key);
    }

    // Options Id type
    if (!isEmpty(selectedId)) {
      $scope.selectedIdType = {
        value: selectedId,
      };
    } else {
      $scope.selectedIdType = {
        value: defaultPlaceId,
      };
      set($scope.appointment, 'person.idType', defaultPlaceId.key);
    }

    // Agenda options
    $scope.daysClosed = [];
    $scope.datesClosed = [];
    if (isAgendaEnabled && $scope.area) {
      $scope.fetchSlots();
      closedDays();
      setCalendarOptions();
    }
    if (!$scope.area) {
      $scope.fetchAreas();
    }

    getCustomFields();
  };

  $scope.fetchSlots = () => {
    const momentDate = moment($scope.appointmentDate.calendar);

    if (!momentDate.isValid()) {
      return NotificationService.Warning($translate.instant('select_date_area_and_apply_filters'));
    }

    const selectedDate = momentDate.format('YYYY-MM-DD');

    AreaService.GetSlotsByHour({
      areaIds: [$scope.area._id],
      hideEmptySlots: false,
      showWorkingDay: false,
      date: selectedDate,
      addNextDay: true,
      placeId: $scope.place._id,
    }).then((slots) => {
      $scope.slotToday = slots.today;
      $scope.placeUTCOffset = $scope.slotToday.length
        ? moment($scope.slotToday[0].hour).tz($scope.place.timezone).format('Z').toString()
        : moment.tz($scope.place.timezone).format('Z').toString();
      $scope.overbookingEnabled = get($scope.place, 'configuration.overbooking.enabled', false);
      const overbookingQuantity = get($scope.place, 'configuration.overbooking.quantity', 1);
      $scope.fetchSlotsExecuted = true;
      let firstDate = true;

      $scope.slots = filter(slots.today, (slot, index, array) => {
        let availability = true;

        if (
          selectedDate ===
          moment(slot.hour).tz($scope.place.timezone).startOf('day').format('YYYY-MM-DD')
        ) {
          const appointmentDateUTCFormat = moment($scope.appointment.appointment.date).utc();
          const appointmentEndDateUTCFormat = moment($scope.appointment.appointment.endDate).utc();

          for (let i = 0; i < $scope.area.appointmentsConfiguration.timeSlotsQuantity; i++) {
            let currentSlot = false;

            const thisSlot = array[index + i];
            const nextSlot = array[index + i + 1];
            const lastSlotQuantity =
              $scope.area.appointmentsConfiguration.timeSlotsQuantity !== i + 1;

            if (
              !thisSlot ||
              (thisSlot &&
                nextSlot &&
                lastSlotQuantity &&
                moment(thisSlot.hour)
                  .utc()
                  .add($scope.area.appointmentsConfiguration.duration, 'minutes')
                  .format() !== moment(nextSlot.hour).utc().format())
            ) {
              availability = false;

              break;
            }

            const thisSlotHourUTC = moment(thisSlot.hour).utc();

            if (
              thisSlotHourUTC.isBetween(
                appointmentDateUTCFormat,
                appointmentEndDateUTCFormat,
                null,
                '[)',
              )
            ) {
              currentSlot = true;
              firstDate = false;

              if (thisSlotHourUTC.isSame(appointmentDateUTCFormat)) {
                $scope.appointmentDate.selected = slot.hour;
              }
            }
            thisSlot.hiddenLabelOverbooking = !appointment.overbooking && currentSlot;

            const now = moment().tz($scope.place.timezone).format();
            const slotTimezone = moment(thisSlot.hour).tz($scope.place.timezone).format();
            const isInThePast = moment(now).isAfter(slotTimezone);

            const realTotalSlots = $scope.overbookingEnabled
              ? thisSlot.slots + overbookingQuantity
              : thisSlot.slots;

            if (!(currentSlot || (thisSlot.reserved < realTotalSlots && !isInThePast))) {
              availability = false;

              break;
            }
          }
        } else {
          availability = false;
        }

        return availability;
      });

      if (firstDate && $scope.slots.length) {
        $scope.appointmentDate.selected = $scope.slots[0].hour;
        $timeout(() => ($scope.overbooking = $scope.slots[0].overbooking));
      } else if (!$scope.slots.length) {
        $scope.appointmentDate.selected = null;
      }

      validateProcessConfig();
    });
  };

  $scope.fetchAreas = () => {
    $scope.areas = [];
    AreaService.GetAgenda()
      .then((areas) => {
        $timeout(() => {
          Object.keys(areas).forEach((key) => {
            if (areas[key].appointmentsConfiguration.enabled && areas[key].open) {
              $scope.areas.push(areas[key]);
            }
          });
        });
      })
      .catch(ErrorService.handler);
  };

  $scope.changeSelectedArea = (selectedArea) => {
    $scope.daysClosed = [];
    $scope.datesClosed = [];
    $scope.area = selectedArea;
    set(
      $scope.appointment,
      'notes.label',
      $scope.area.appointmentsConfiguration.notes.label || null,
    );
    closedDays();
    setCalendarOptions();

    $scope.appointmentDate = {
      calendar: DateHandlerService.getNeutralDate(),
      selected: null,
    };
    $scope.fetchSlots();
  };

  $scope.updateIdType = () => {
    $scope.appointment.person.idType = get($scope.selectedIdType, 'value.key', undefined);
  };

  $scope.save = () => {
    $scope.appointment.person.language = $translate.use();
    $scope.appointment.person.identifications = buildIdentifications(
      $scope.appointment.person.id,
      $scope.appointment.person.idType,
    );

    if (!validateCustomFields()) {
      return;
    }

    if (
      $scope.place.configuration.allowMultipleSubscriptions &&
      get($scope.area, 'configuration.multipleSubscriptions.enabled.appointments', false)
    ) {
      const peopleQuantityValidation = ValidationService.validatePeopleQuantity(
        1,
        $scope.area.configuration.multipleSubscriptions.maxPeople,
        $scope.people,
      );
      if (!peopleQuantityValidation) {
        return;
      }
    }
    $scope.appointment.people = $scope.people;
    if (
      ValidationService.validateUserInputData(
        $scope.appointment.person,
        $scope.endUserPersonalData,
        'webAgenda',
        $scope,
        { timeOut: 5000 },
      ) &&
      validate()
    ) {
      $scope.blockSaveButton = true;
      if (isAgendaEnabled) {
        $scope.appointment.appointment.date = $scope.appointmentDate.selected;
        $scope.appointment.appointment.ISODate = $scope.appointmentDate.selected;
      }
      if (appointmentId) {
        $scope.appointment.updatedFrom = CreatedFromEnum.virtualineAgenda;
        AppointmentService.Update($scope.appointment)
          .then(() => {
            $scope.blockSaveButton = false;
            NotificationService.Success($translate.instant('appointment_edit_succ'));
            $uibModalInstance.close('saved');
          })
          .catch((error) => {
            $scope.blockSaveButton = false;
            ErrorService.handler(error);
          });
      } else {
        const overlapAppointmentEnabled = get(
          $scope.place,
          'configuration.overlapAppointment.enabled',
          false,
        );
        if (overlapAppointmentEnabled) {
          overlapAppointmentWarning();
        } else {
          createAppointment();
        }
      }
    }
  };

  $scope.getMaxSubscriptionPopupText = () => {
    const maxPeople = get($scope.area, 'configuration.multipleSubscriptions.maxPeople', 1);
    return `${$translate.instant('people_maximum_quantity')} ${maxPeople}`;
  };

  $scope.cancel = () => {
    $uibModalInstance.close('cancel');
  };

  $scope.init();
};

module.exports = module.controller('EditAppointmentController', EditAppointmentController);
